// Shared frame and small pieces for the catalog screens (products, taxonomy, media). Design tokens
// only; every user-facing string comes from t("catalog.…") or the shared string layer.
import { forwardRef, type ReactNode, type TextareaHTMLAttributes } from "react";
import { t } from "@ady/i18n";
import { ErrorState, StatePanel, controlFrame, cx, useFieldContext } from "@ady/ui";
import { ApiError } from "../api/client";
import { errorMessage } from "../api/errors";
import { useShell } from "../shell/context";
import { can } from "../shell/nav";
import { PageHeader } from "../shell/PageHeader";

export const CTID = {
  productsPage: "catalog-products-page",
  productsTable: "catalog-products-table",
  productsSearch: "catalog-products-search",
  addProduct: "catalog-add-product",
  bulkButton: "catalog-bulk-button",
  bulkDialog: "catalog-bulk-dialog",
  bulkSubmit: "catalog-bulk-submit",
  bulkResult: "catalog-bulk-result",
  editorPage: "catalog-product-editor",
  editorTitle: "catalog-product-title",
  editorSave: "catalog-product-save",
  editorDelete: "catalog-product-delete",
  editorDuplicate: "catalog-product-duplicate",
  editorStatus: "catalog-product-status",
  optionName: (i: number) => `catalog-option-name-${i}`,
  optionValues: (i: number) => `catalog-option-values-${i}`,
  addOption: "catalog-add-option",
  variantRow: "catalog-variant-row",
  variantPrice: (i: number) => `catalog-variant-price-${i}`,
  variantCost: (i: number) => `catalog-variant-cost-${i}`,
  photoUpload: "catalog-photo-upload",
  photoItem: "catalog-photo-item",
  categoriesPage: "catalog-categories-page",
  brandsPage: "catalog-brands-page",
  collectionsPage: "catalog-collections-page",
  mediaPage: "catalog-media-page",
  mediaItem: "catalog-media-item",
  addButton: "catalog-add-button",
  nameInput: "catalog-name-input",
  dialogSubmit: "catalog-dialog-submit",
  listRow: "catalog-list-row",
  errorState: "error-state",
  emptyState: "empty-state",
  loadingState: "loading-state",
} as const;

/** Store-scoped page frame: heading, lead, actions and the permission gate (IA §2.2). */
export function CatalogPage({
  title,
  lead,
  anyOf,
  testId,
  canonical,
  actions,
  wide,
  children,
}: {
  title: string;
  lead?: ReactNode;
  anyOf: string[];
  testId: string;
  canonical: string;
  actions?: ReactNode;
  wide?: boolean;
  children: ReactNode;
}) {
  const { membership } = useShell();
  if (!can(membership, anyOf)) {
    return (
      <StatePanel
        kind="permission-denied"
        size="page"
        headingLevel={2}
        title={t("states.permissionDeniedTitle")}
        body={t("states.permissionDeniedBody", { who: t("states.permissionWhoOwner") })}
        data-testid={testId}
      />
    );
  }
  return (
    <div
      className={cx("flex w-full flex-col gap-4", wide ? "" : "max-w-4xl")}
      data-testid={testId}
      data-canonical={canonical}
    >
      <PageHeader title={title} lead={lead} actions={actions} />
      {children}
    </div>
  );
}

export function Card({
  title,
  aside,
  id,
  children,
  className,
}: {
  title: string;
  aside?: ReactNode;
  id: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cx(
        "flex flex-col gap-4 rounded-xs border border-card bg-surface p-4 sm:p-5",
        className,
      )}
      aria-labelledby={`${id}-h`}
    >
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id={`${id}-h`} className="text-md font-bold">
          {title}
        </h2>
        {aside ? <span className="text-sm text-fg-secondary">{aside}</span> : null}
      </header>
      {children}
    </section>
  );
}

/** A textarea wired to the surrounding <Field> (label, help, error, aria). */
export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(function Textarea({ className, id, ...rest }, ref) {
  const field = useFieldContext();
  return (
    <div className={cx(controlFrame, field?.invalid ? "border-danger-fg" : "border-control")}>
      <textarea
        ref={ref}
        id={id ?? field?.id}
        aria-invalid={field?.invalid || undefined}
        aria-describedby={field?.describedBy}
        data-content="user"
        {...rest}
        className={cx(
          "min-h-28 w-full resize-y bg-transparent px-3 py-2.5 text-md outline-none",
          className,
        )}
      />
    </div>
  );
});

export function LoadError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  return (
    <ErrorState
      size="inline"
      headingLevel={3}
      body={errorMessage(error)}
      requestId={error instanceof ApiError ? error.requestId : undefined}
      onRetry={onRetry}
      data-testid={CTID.errorState}
    />
  );
}

/** Copy for catalog API errors (codes and field codes from packages/domain/product). */
export function catalogError(err: unknown): string {
  if (err instanceof ApiError) {
    const f = err.fields ?? {};
    if (f.sku === "taken") return t("catalog.errors.skuTaken");
    if (f.barcode === "taken") return t("catalog.errors.barcodeTaken");
    if (f.slug === "taken") return t("catalog.errors.slugTaken");
    if (f.media) return t("catalog.errors.mediaUnknown");
    if (f.variants === "duplicate_combination") return t("catalog.errors.combination");
    if (err.code === "authz.forbidden" && err.details?.permission === "products.cost.read")
      return t("catalog.errors.costForbidden");
    if (err.code === "entitlement.quota_exceeded") return t("catalog.errors.quota");
    if (err.code === "precondition.version_mismatch") return t("catalog.editor.conflict");
  }
  return errorMessage(err);
}

export const splitList = (s: string): string[] => [
  ...new Set(
    s
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean),
  ),
];

export function useStoreIds() {
  const { membership, store } = useShell();
  return { orgId: membership.org.id, storeId: store.id };
}
